/**
 * Config - Centralized Configuration Management
 * 
 * This module manages all application configuration, pulling from
 * environment variables and providing defaults.
 * 
 * @module core/Config
 */

// Lazy load StorageService to avoid circular dependency
let StorageService;
let containerRef;
try {
  // Prefer DI container if available
  containerRef = (window.DIContainer || window.container || undefined);
} catch(_) {}

// Minimal localStorage adapter (works in PROD when DI/window singletons are unavailable)
const localStorageAdapter = {
  get(key) {
    try {
      if (typeof localStorage === 'undefined') return '';
      const raw = localStorage.getItem(key);
      if (raw == null) return '';
      try { return JSON.parse(raw); } catch { return raw; }
    } catch { return '';
    }
  }
};

const getStorageService = () => {
  if (!StorageService) {
    try {
      const fromContainer = containerRef?.resolve?.('storage') || containerRef?.get?.('storage');
      if (fromContainer) {
        StorageService = fromContainer;
      } else if (typeof window !== 'undefined' && window.StorageService) {
        StorageService = window.StorageService;
      } else {
        StorageService = localStorageAdapter;
      }
    } catch(_) {
      StorageService = (typeof window !== 'undefined' && window.StorageService) ? window.StorageService : localStorageAdapter;
    }
  }
  return StorageService;
};

/**
 * Application configuration object
 * Merges environment variables with defaults
 */
export const Config = {
  // Application metadata
  APP_NAME: import.meta.env.VITE_APP_NAME || 'Strategic Intelligence Platform',
  // Default: PROD=5.3.2, DEV=5.4.0-dev (can be overridden via VITE_APP_VERSION)
  APP_VERSION: import.meta.env.VITE_APP_VERSION || (import.meta.env?.DEV ? '5.4.2-dev' : '5.3.2'),
  APP_ENV: import.meta.env.VITE_APP_ENV || 'development',
  
  // Control Plane (optional; enables usage tracking + branding)
  CONTROL_PLANE: {
    BASE: import.meta.env.VITE_CP_BASE || '',
    ORG_ID: import.meta.env.VITE_ORG_ID || '',
    WORKSPACE_ID: import.meta.env.VITE_WORKSPACE_ID || ''
  },
  
  // Optional: remote prompts JSON (for shared pilot prompts)
  REMOTE_PROMPTS_URL: (import.meta.env.VITE_PROMPTS_URL || ''),
  
  // API Configuration
  API: {
    OPENAI: {
      // Try to get from env, then StorageService, then legacy location
      get KEY() {
        if (import.meta.env.VITE_OPENAI_API_KEY) {
          return import.meta.env.VITE_OPENAI_API_KEY;
        }
        const storage = getStorageService();
        if (storage) {
          return storage.get('apiKey') || '';
        }
        // Fallback to legacy location
        return localStorage.getItem('openai_api_key') || '';
      },
      MODEL: import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o',
      TEMPERATURE: parseFloat(import.meta.env.VITE_OPENAI_TEMPERATURE) || 0.7,
      MAX_TOKENS: 4000,
      ENDPOINT: (import.meta.env.VITE_OPENAI_API_BASE || 'https://api.openai.com/v1/chat/completions')
    },
    TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000,
    MAX_RETRIES: parseInt(import.meta.env.VITE_MAX_RETRIES) || 3
  },
  
  // Feature flags
  FEATURES: {
    DEBUG: import.meta.env.VITE_ENABLE_DEBUG === 'true',
    ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
    ERROR_REPORTING: import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true',
    // New feature flags (default off in PROD unless explicitly enabled)
    RBAC: import.meta.env.VITE_FEATURE_RBAC === 'true',
    INSIGHTS_TAB: import.meta.env.VITE_FEATURE_INSIGHTS_TAB === 'true',
    BUSINESS_LEVERS: import.meta.env.VITE_FEATURE_BUSINESS_LEVERS === 'true',
    // New flag to enable Causality (CLD). For backward-compatibility,
    // it mirrors BUSINESS_LEVERS when not explicitly set.
    CAUSALITY: (import.meta.env.VITE_FEATURE_CAUSALITY === 'true') || (import.meta.env.VITE_FEATURE_BUSINESS_LEVERS === 'true'),
    // Enable 3D causality canvas (react-force-graph-3d)
    CAUSALITY_3D: (import.meta.env.VITE_FEATURE_CAUSALITY_3D === 'true'),
    // Read-only exploration mode for causality canvases
    CAUSALITY_READ_ONLY: (import.meta.env.VITE_CAUSALITY_READ_ONLY === 'true'),
    CAUSALITY_AUTO_LAYOUT: import.meta.env.VITE_CAUSALITY_AUTO_LAYOUT === 'true',
    METRICS_CHAT: import.meta.env.VITE_FEATURE_METRICS_CHAT === 'true',
    DEPENDENCIES: import.meta.env.VITE_FEATURE_DEPENDENCIES === 'true',
    RISKS: import.meta.env.VITE_FEATURE_RISKS === 'true',
    THEME_ENTERPRISE: import.meta.env.VITE_THEME_ENTERPRISE === 'true',
    AUTO_SAVE: true,
    KEYBOARD_SHORTCUTS: true,
    TRANSFER_SYSTEM: true,
    CONVERSATION_MEMORY: false,
    // Keep legacy middleware STATE_CHANGED emission disabled by default
    EVENT_BRIDGE_STATE: false
  },
  
  // Panel migration configuration
  PANEL_MIGRATION: {
    enabled: true, // Enable unified blue headers globally
    version: '2.0',
    compatMode: true, // Keep backward compatibility
    modules: {
      insights: true,    // Already using createPanel
      strategy: true,    // Already using createPanel  
      initiatives: true, // Enable blue headers
      portfolio: true,   // Enable blue headers
      settings: true,    // Enable blue headers
      chat: true,        // Enable blue headers
      levers: true       // Enable blue headers
    }
  },
  
  // UI Configuration
  UI: {
    TOAST_DURATION: parseInt(import.meta.env.VITE_TOAST_DURATION) || 3000,
    ANIMATION_DURATION: parseInt(import.meta.env.VITE_ANIMATION_DURATION) || 300,
    DEBOUNCE_DELAY: parseInt(import.meta.env.VITE_DEBOUNCE_DELAY) || 300,
    MAX_CHAT_MESSAGES: 100,
    TEXTAREA_MAX_HEIGHT: 150,
    SIDEBAR_WIDTH: 320
  },
  
  // Initiative types configuration
  INITIATIVE_TYPES: [
    { value: 'Goal', color: '#28a745', icon: '🎯' },
    { value: 'Challenge', color: '#dc3545', icon: '🚧' },
    { value: 'Demand', color: '#ffc107', icon: '📢' },
    { value: 'Initiative', color: '#005EB8', icon: '🚀' }
  ],
  
  // Tab configuration
  TABS: [
    { 
      id: 'strategic-framework',
      label: 'Strategic Framework',
      icon: '📊',
      component: 'StrategicFramework'
    },
    {
      id: 'objectives',
      label: 'Objectives & Initiatives',
      icon: '🎯',
      component: 'ObjectivesInitiatives'
    },
    {
      id: 'portfolio',
      label: 'Portfolio Analysis',
      icon: '📈',
      component: 'PortfolioAnalysis'
    },
    {
      id: 'business-levers',
      label: 'Business Levers',
      icon: '🧩',
      component: 'BusinessLevers',
      // Gate via new CAUSALITY flag (backward-compatible mapping above)
      featureFlag: 'CAUSALITY'
    },
    {
      id: 'insights',
      label: 'Insights',
      icon: '📊',
      component: 'Insights',
      // Visible to admin and tuner only when enabled
      rolesAllowed: ['admin', 'tuner'],
      featureFlag: 'INSIGHTS_TAB'
    },
    {
      id: 'settings',
      label: 'Settings & Admin',
      icon: '⚙️',
      component: 'SettingsAdmin',
      // Only admin should see Settings
      rolesAllowed: ['admin']
    }
  ],
  
  // File upload configuration
  FILE_UPLOAD: {
    MAX_SIZE: 10 * 1024 * 1024, // 10MB
    ALLOWED_TYPES: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'application/json'
    ]
  },
  
  // Storage keys
  STORAGE_KEYS: {
    APP_STATE: 'appState',
    API_KEY: 'openai_api_key',
    USER_PREFERENCES: 'userPreferences',
    INITIATIVES: 'goalsDatabase',
    UPLOADED_STRATEGY: 'uploadedStrategy',
    EMERGENT_STRATEGY: 'emergentStrategy',
    CHAT_HISTORY: 'chatHistory',
    PROMPT_MANAGER: 'promptManagerData'
  },
  
  // Default prompts
  DEFAULT_PROMPTS: {
    outcomeAssistant: {
      systemRole: `You are an expert strategic business consultant focused on outcomes over outputs.
Your role is to help users articulate clear, measurable business objectives that create real value.

Key principles:
- Always focus on business outcomes (the "why") rather than activities (the "what")
- Challenge users to think about measurable impact and value creation
- Use the "So what?" test - if an objective doesn't clearly create value, help refine it
- Guide users toward SMART objectives (Specific, Measurable, Achievable, Relevant, Time-bound)

When helping users:
1. First understand the business problem or opportunity
2. Identify the desired future state and value creation
3. Define clear success metrics tied to business impact
4. Ensure objectives are outcome-focused, not activity-based

Example transformations:
- Activity: "Implement new CRM system" → Outcome: "Reduce customer churn by 15% through improved relationship management"
- Activity: "Train sales team" → Outcome: "Increase average deal size by 20% through enhanced consultative selling"`,
      temperature: 0.7
    },
    portfolioAnalysis: {
      systemRole: `You are a strategic portfolio analyst. Analyze the initiative portfolio for:
1. Strategic alignment and gaps
2. Resource conflicts and overlaps
3. Risk concentration
4. Optimization opportunities
Provide executive-level insights and actionable recommendations.`,
      temperature: 0.6
    },
    riskAnalysis: {
      systemRole: `You are a risk management expert. Analyze initiatives for potential risks including:
- Technical risks
- Market risks  
- Operational risks
- Financial risks
Provide risk ratings and mitigation strategies.`,
      temperature: 0.5
    }
  },
  
  // Validation rules
  VALIDATION: {
    OBJECTIVE_MIN_LENGTH: 10,
    OBJECTIVE_MAX_LENGTH: 500,
    METRICS_MIN: 1,
    METRICS_MAX: 10,
    OWNER_MIN_LENGTH: 2,
    OWNER_MAX_LENGTH: 100
  },
  
  // Performance settings
  PERFORMANCE: {
    BATCH_SIZE: 50,
    VIRTUAL_SCROLL_THRESHOLD: 100,
    CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
    SEARCH_DEBOUNCE: 300
  }
};

/**
 * Update configuration at runtime
 * @param {Object} updates - Configuration updates
 */
export function updateConfig(updates) {
  Object.keys(updates).forEach(key => {
    if (key in Config) {
      if (typeof Config[key] === 'object' && !Array.isArray(Config[key])) {
        Config[key] = { ...Config[key], ...updates[key] };
      } else {
        Config[key] = updates[key];
      }
    }
  });
}

/**
 * Get configuration value by path
 * @param {string} path - Dot notation path (e.g., 'API.OPENAI.KEY')
 * @param {*} defaultValue - Default value if path not found
 * @returns {*} Configuration value
 */
export function getConfig(path, defaultValue = undefined) {
  return path.split('.').reduce((obj, key) => obj?.[key], Config) ?? defaultValue;
}

/**
 * Check if feature is enabled
 * @param {string} feature - Feature name
 * @returns {boolean} Whether feature is enabled
 */
export function isFeatureEnabled(feature) {
  return Config.FEATURES[feature] === true;
}

/**
 * Validate configuration
 * @returns {Object} Validation result
 */
Config.validateConfig = function() {
  const errors = [];
  const warnings = [];
  
  // Check required configurations
  if (!Config.API.OPENAI.KEY) {
    warnings.push('OpenAI API key not configured');
  }
  
  // Check environment
  if (Config.APP_ENV === 'production' && Config.FEATURES.DEBUG) {
    warnings.push('Debug mode enabled in production');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

// Keep as export for compatibility
export const validateConfig = Config.validateConfig;

// Export default (Config is already exported as named export at the top)
export default Config;

// Attach to window in development for debugging
if (import.meta.env.DEV) {
  window.Config = Config;
  console.log('🔧 Config available at window.Config for debugging');
}
